import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

// Produits éligibles au tapis offert : litière connectée et litière ouverte
const ELIGIBLE_PRODUCT_IDS = [10392576164181, 10390755180885];

// Variante cadeau : Tapis de litière pour chat, Noir / 40x50 cm
const GIFT_VARIANT_ID = 52590211465557;

// Marqueur technique permettant de distinguer la ligne ajoutée automatiquement
// d'un ajout manuel de la même variante par le client.
const GIFT_PROPERTY_KEY = '_litierechat_gift';

// Mémorisation à l'échelle de la session (sessionStorage) :
// - DECLINED : le client a retiré volontairement le tapis cadeau -> ne pas le remettre.
// - SEEN     : la ligne cadeau a déjà été constatée présente pour le panier éligible
//              en cours. Sert à distinguer « cadeau jamais ajouté » (on l'ajoute)
//              de « cadeau ajouté puis disparu » (= suppression volontaire).
// Les deux drapeaux sont remis à zéro dès qu'il n'y a plus aucune litière éligible
// dans le panier (nouveau cycle d'achat).
const STORAGE_DECLINED = 'litierechat_gift_declined';
const STORAGE_SEEN = 'litierechat_gift_seen';

let isSyncing = false;
let needsResync = false;

// Échec d'ajout du cadeau (rupture de stock…) : on arrête de réessayer pour la
// durée de vie de la page, sans bloquer définitivement (un rechargement retentera).
let giftAddFailed = false;

// Miroir en mémoire : garantit un comportement correct même si sessionStorage
// est indisponible (navigation privée), au moins pour la durée de vie de la page.
const memoryFlags = Object.create(null);

function readFlag(key) {
  try {
    const stored = window.sessionStorage.getItem(key);
    if (stored !== null) return stored === '1';
  } catch (error) {
    /* stockage indisponible : on retombe sur le miroir mémoire */
  }
  return memoryFlags[key] === true;
}

function writeFlag(key, value) {
  memoryFlags[key] = value === true;
  try {
    if (value) {
      window.sessionStorage.setItem(key, '1');
    } else {
      window.sessionStorage.removeItem(key);
    }
  } catch (error) {
    /* navigation privée / stockage indisponible : le miroir mémoire prend le relais */
  }
}

function getCart() {
  return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
}

// Ne renvoie QUE les lignes ajoutées automatiquement (propriété technique présente).
// Un tapis identique ajouté manuellement par le client n'a pas cette propriété et
// n'est donc jamais retourné ici : le script n'y touche jamais.
function findGiftLines(cart) {
  return cart.items.filter(
    (item) => item.variant_id === GIFT_VARIANT_ID && item.properties && item.properties[GIFT_PROPERTY_KEY] === '1',
  );
}

function hasEligibleLitiere(cart) {
  return cart.items.some((item) => ELIGIBLE_PRODUCT_IDS.includes(item.product_id));
}

function getCartDrawerSectionId() {
  return document.querySelector('cart-drawer')?.dataset.sectionId;
}

function isOnCartPage() {
  return document.getElementById('cart_form') != null;
}

async function addGiftLine(previousItemCount) {
  const sectionId = getCartDrawerSectionId();
  const body = {
    items: [
      {
        id: GIFT_VARIANT_ID,
        quantity: 1,
        properties: { [GIFT_PROPERTY_KEY]: '1' },
      },
    ],
  };

  if (sectionId) {
    body.sections = sectionId;
    body.sections_url = window.location.pathname;
  }

  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok) {
    giftAddFailed = true;
    console.error("[auto-gift] Impossible d'ajouter le tapis offert :", data.description || data.message || data);
    return;
  }

  // Enregistré immédiatement pour qu'une resynchro déclenchée par cet ajout
  // ne considère pas l'absence transitoire comme un refus client.
  writeFlag(STORAGE_SEEN, true);

  document.dispatchEvent(
    new CartUpdatedEvent(
      {},
      {
        itemCount: previousItemCount + 1,
        source: 'auto-gift',
        sections: data.sections,
      },
    ),
  );

  if (isOnCartPage()) window.location.reload();
}

async function removeGiftLine(giftLine, previousItemCount) {
  const sectionId = getCartDrawerSectionId();
  const body = {
    id: giftLine.key,
    quantity: 0,
  };

  if (sectionId) {
    body.sections = sectionId;
    body.sections_url = window.location.pathname;
  }

  const response = await fetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok || data.errors) {
    console.error('[auto-gift] Impossible de retirer le tapis offert :', data.errors || data.description || data.message || data);
    return;
  }

  document.dispatchEvent(
    new CartUpdatedEvent(
      {},
      {
        itemCount: previousItemCount - giftLine.quantity,
        source: 'auto-gift',
        sections: data.sections,
      },
    ),
  );

  if (isOnCartPage()) window.location.reload();
}

async function syncGiftLine() {
  if (isSyncing) {
    needsResync = true;
    return;
  }

  isSyncing = true;

  try {
    const cart = await getCart();
    const eligible = hasEligibleLitiere(cart);
    const giftLines = findGiftLines(cart);

    if (!eligible) {
      // H : plus aucune litière éligible -> retirer UNIQUEMENT la (les) ligne(s)
      // cadeau marquée(s). Le tapis acheté manuellement n'est jamais concerné.
      for (const giftLine of giftLines) {
        await removeGiftLine(giftLine, cart.item_count);
      }
      // D : nouveau cycle d'achat -> on oublie le refus et l'historique.
      writeFlag(STORAGE_DECLINED, false);
      writeFlag(STORAGE_SEEN, false);
      giftAddFailed = false;
      return;
    }

    if (giftLines.length > 0) {
      // Cadeau présent : on le mémorise ; il n'est donc pas (ou plus) refusé.
      writeFlag(STORAGE_SEEN, true);
      writeFlag(STORAGE_DECLINED, false);

      // F : au plus une ligne cadeau automatique -> on retire les doublons éventuels.
      if (giftLines.length > 1) {
        for (const duplicateLine of giftLines.slice(1)) {
          await removeGiftLine(duplicateLine, cart.item_count);
        }
      }
      return;
    }

    // Ici : au moins une litière éligible, aucune ligne cadeau automatique.

    if (readFlag(STORAGE_DECLINED)) {
      // B / C : le client a retiré le cadeau volontairement -> on ne le remet pas
      // (changement de quantité, ouverture/fermeture du panier, navigation…).
      return;
    }

    if (readFlag(STORAGE_SEEN)) {
      // Le cadeau avait été ajouté puis a disparu alors qu'une litière éligible
      // reste dans le panier : c'est une suppression volontaire depuis le panier.
      writeFlag(STORAGE_DECLINED, true);
      writeFlag(STORAGE_SEEN, false);
      return;
    }

    if (giftAddFailed) {
      // Ajout déjà tenté sans succès sur cette page -> on n'insiste pas.
      return;
    }

    // A / E : première fois pour ce panier éligible -> ajout du tapis cadeau.
    await addGiftLine(cart.item_count);
  } catch (error) {
    console.error('[auto-gift]', error);
  } finally {
    isSyncing = false;

    if (needsResync) {
      needsResync = false;
      syncGiftLine();
    }
  }
}

document.addEventListener(ThemeEvents.cartUpdated, () => syncGiftLine());

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => syncGiftLine(), { once: true });
} else {
  syncGiftLine();
}
